import type {
  ResourceBuiltinDetail,
  ResourceBuiltinIngressBackend,
  ResourceBuiltinLink,
  ResourceBuiltinStringList,
  ResourceBuiltinTargetPort,
  ResourceBuiltinValue,
} from "@k8s-dashboard/contracts";
import { useT, type Translate } from "@/i18n/I18nProvider";
import { DataTable, Facts, Section } from "./ResourceDetailSections";
import type {
  BuiltinRelationGroup,
  BuiltinRelationsView,
} from "./builtin-relations";

const VALUE_STATE_ID = {
  absent: "resources.job.absent",
  invalid: "resources.value.invalid",
  withheld_too_large: "resources.value.withheld",
} as const;
const PROJECTION_STATE_ID = {
  unsupported: "resources.state.unsupported",
  malformed: "resources.state.malformed",
  projection_too_large: "resources.state.projection_too_large",
} as const;
const ENDPOINT_REASON_ID = {
  syncing: "resources.endpoint.syncing",
  forbidden: "resources.endpoint.forbidden",
  missing: "resources.endpoint.missing",
  unsupported: "resources.endpoint.unsupported",
  endpoint_ready_count_not_exposed:
    "resources.endpoint.endpoint_ready_count_not_exposed",
} as const;
const PVC_PHASE_ID = {
  Pending: "resources.pvc.phase.Pending",
  Bound: "resources.pvc.phase.Bound",
  Lost: "resources.pvc.phase.Lost",
} as const;
const ACCESS_MODE_ID = {
  ReadWriteOnce: "resources.pvc.accessMode.ReadWriteOnce",
  ReadOnlyMany: "resources.pvc.accessMode.ReadOnlyMany",
  ReadWriteMany: "resources.pvc.accessMode.ReadWriteMany",
  ReadWriteOncePod: "resources.pvc.accessMode.ReadWriteOncePod",
} as const;
const CONDITION_STATUS_ID = {
  True: "resources.condition.true",
  False: "resources.condition.false",
  Unknown: "resources.condition.unknown",
} as const;
const RELATION_QUERY_STATE_ID = {
  loading: "resources.relations.loading",
  unsupported: "resources.relations.unsupported",
  forbidden: "resources.relations.forbidden",
  error: "resources.relations.error",
  identity_mismatch: "resources.relations.identity_mismatch",
} as const;
const RELATION_DISPLAY_STATE_ID = {
  ready: "resources.relations.ready",
  empty: "resources.relations.empty",
  partial: "resources.relations.partial",
  unsupported: "resources.relations.unsupported",
} as const;
const RELATION_GROUP_ID = {
  pods: "resources.relations.pods",
  volume: "resources.relations.volume",
  claims: "resources.relations.claims",
  volumes: "resources.relations.volumes",
} as const;

type Omitted = { omitted?: number; lowerBound?: boolean };
type TableSpec = {
  caption: string;
  headers: string[];
  rows: { key: string; cells: React.ReactNode[] }[];
};
type DetailSpec = {
  key: string;
  title: string;
  rows?: [string, React.ReactNode][];
  table?: TableSpec;
  children?: React.ReactNode;
};

function valueText(t: Translate, value: ResourceBuiltinValue): string {
  if (value.state !== "ok") return t(VALUE_STATE_ID[value.state]);
  if (value.value === undefined) return t("resources.value.unknown");
  return `${value.value}${value.truncated ? ` ${t("resources.job.truncated")}` : ""}`;
}

function listText(
  t: Translate,
  list: ResourceBuiltinStringList,
  unit: string,
): string {
  const values = list.items.length
    ? list.items.join(", ")
    : t("resources.job.absent");
  if (!list.omitted) return values;
  return `${values} · ${t("resources.value.omitted", { count: list.omitted, unit })}${list.lowerBound ? ` ${t("resources.value.lowerBound")}` : ""}`;
}

function boolText(t: Translate, value: boolean | undefined): string {
  if (value === undefined) return t("resources.job.absent");
  return t(value ? "resources.value.true" : "resources.value.false");
}

function pvcPhaseText(t: Translate, phase: ResourceBuiltinValue): string {
  if (phase.state !== "ok") return valueText(t, phase);
  const value = phase.value;
  return value === "Pending" || value === "Bound" || value === "Lost"
    ? t(PVC_PHASE_ID[value])
    : t("resources.value.invalid");
}

function accessModesText(
  t: Translate,
  modes: ResourceBuiltinStringList,
): string {
  const items = modes.items.map((mode) =>
    typeof mode === "string" && Object.hasOwn(ACCESS_MODE_ID, mode)
      ? t(ACCESS_MODE_ID[mode as keyof typeof ACCESS_MODE_ID])
      : t("resources.value.invalid"),
  );
  return listText(t, { ...modes, items }, t("resources.pvc.accessModes"));
}

function targetPortText(
  t: Translate,
  target: ResourceBuiltinTargetPort,
): string {
  if (target.state !== "ok") return valueText(t, target);
  if (target.type === "name")
    return t("resources.port.named", { value: target.name ?? "" });
  if (target.type === "number")
    return t("resources.port.numeric", { value: target.number ?? "" });
  return t("resources.value.invalid");
}

function backendText(
  t: Translate,
  backend: ResourceBuiltinIngressBackend,
): string {
  if (backend.state === "unsupported_backend")
    return t("resources.ingress.backend.unsupported");
  if (backend.state === "invalid") return t("resources.value.invalid");
  if (backend.state === "absent") return t("resources.job.absent");
  const port =
    backend.servicePort.state === "ok"
      ? backend.servicePort.type === "name"
        ? backend.servicePort.name
        : backend.servicePort.number
      : valueText(t, backend.servicePort);
  return `${valueText(t, backend.serviceName)}:${port ?? ""}`;
}

function OmittedNote({ value, unit }: { value: Omitted; unit: string }) {
  const { t } = useT("resources");
  if (!value.omitted) return null;
  const text = `${t("resources.value.omitted", {
    count: value.omitted,
    unit,
  })}${value.lowerBound ? ` ${t("resources.value.lowerBound")}` : ""}`;
  return (
    <p className="muted" role="note">
      {text}
    </p>
  );
}

function StateBox({
  state,
  text,
  error = false,
}: {
  state: string;
  text: string;
  error?: boolean;
}) {
  return (
    <div
      className={`state${error ? " state--warning" : ""}`}
      role={error ? "alert" : "note"}
      data-state={state}
    >
      <span className="state__glyph" aria-hidden="true">
        {error ? "▲" : state === "ready" || state === "empty" ? "✓" : "•"}
      </span>
      <span className="state__title">{text}</span>
    </div>
  );
}

function DetailSection({ title, rows, table, children }: DetailSpec) {
  return (
    <Section title={title}>
      {rows?.length ? <Facts rows={rows} /> : null}
      {table?.rows.length ? (
        <DataTable
          caption={table.caption}
          headers={table.headers.map((header) => [header])}
          rows={table.rows}
        />
      ) : null}
      {children}
    </Section>
  );
}

function relationSummary(t: Translate, group: BuiltinRelationGroup): string {
  const confirmed = group.lowerBound
    ? t("resources.relations.confirmedLowerBound", { count: group.confirmed })
    : t("resources.relations.confirmed", { count: group.confirmed });
  return `${t("resources.relations.shown", { count: group.items.length })} / ${confirmed}`;
}

function RelationsSection({
  t,
  relations,
  onResourceLink,
}: {
  t: Translate;
  relations: BuiltinRelationsView;
  onResourceLink?: (link: ResourceBuiltinLink) => void;
}) {
  if (relations.state !== "ready") {
    return (
      <Section title={t("resources.relations")}>
        <StateBox
          state={relations.state}
          text={t(RELATION_QUERY_STATE_ID[relations.state])}
          error={relations.state !== "loading"}
        />
      </Section>
    );
  }
  return (
    <Section title={t("resources.relations")}>
      {relations.groups.map((group) => {
        const effective =
          group.state === "unsupported"
            ? "unsupported"
            : group.state === "partial"
              ? "partial"
              : group.confirmed === 0
                ? "empty"
                : "ready";
        return (
          <div key={group.id} data-state={effective}>
            <h4>{t(RELATION_GROUP_ID[group.id])}</h4>
            <StateBox
              state={effective}
              text={t(RELATION_DISPLAY_STATE_ID[effective])}
              error={effective === "partial" || effective === "unsupported"}
            />
            {group.state !== "unsupported" && (
              <p className="muted" role="note">
                {relationSummary(t, group)}
                {group.truncated
                  ? ` · ${t("resources.relations.truncated")}`
                  : ""}
                {group.hidden
                  ? ` · ${t("resources.relations.hidden", { count: group.hidden })}`
                  : ""}
                {group.unknownEvidence
                  ? ` · ${t("resources.relations.unknownEvidence")}`
                  : ""}
                {group.unresolved
                  ? ` · ${t("resources.relations.unresolved", { count: group.unresolved })}`
                  : ""}
              </p>
            )}
            {group.items.length > 0 && (
              <DataTable
                caption={t(RELATION_GROUP_ID[group.id])}
                headers={[
                  [t("resources.def.kind")],
                  [t("resources.field.namespace")],
                  [t("resources.col.name")],
                ]}
                rows={group.items.map((row) => ({
                  key: row.key,
                  cells: [
                    row.kind,
                    row.namespace ?? "-",
                    row.link && onResourceLink ? (
                      <button
                        type="button"
                        className="linkish"
                        onClick={() =>
                          onResourceLink(row.link as ResourceBuiltinLink)
                        }
                      >
                        {row.name}
                      </button>
                    ) : (
                      `${row.name}${row.resolved ? "" : ` · ${t("resources.relations.unresolvedOne")}`}`
                    ),
                  ],
                }))}
              />
            )}
          </div>
        );
      })}
      {relations.relationWideOmittedEdges.map((omission) => (
        <p className="muted" role="note" key={omission.relation}>
          {t("resources.relations.relationWideOmitted", {
            relation: omission.relation,
            count: omission.count,
          })}
          {omission.lowerBound
            ? ` ${t("resources.relations.lower_bound")}`
            : ""}
        </p>
      ))}
    </Section>
  );
}

export function ResourceBuiltinDetailSections({
  builtin,
  relations,
  onResourceLink,
}: {
  builtin: ResourceBuiltinDetail;
  relations?: BuiltinRelationsView;
  onResourceLink?: (link: ResourceBuiltinLink) => void;
}) {
  const { t } = useT("resources");
  if (builtin.state !== "ok") {
    return (
      <Section title={t("resources.builtin")}>
        <StateBox
          state={builtin.state}
          text={t(PROJECTION_STATE_ID[builtin.state])}
          error
        />
      </Section>
    );
  }

  const sections: DetailSpec[] = [];
  const metadata = builtin.metadata;
  if (metadata) {
    const owners = metadata.ownerReferences;
    sections.push({
      key: "metadata",
      title: t("resources.metadata"),
      rows: [
        [
          t("resources.metadata.labels"),
          `${metadata.labelCount} · ${listText(t, metadata.labelKeys, t("resources.metadata.labels"))}`,
        ],
        [
          t("resources.metadata.annotations"),
          `${metadata.annotationCount} · ${listText(t, metadata.annotationKeys, t("resources.metadata.annotations"))}`,
        ],
      ],
      table: {
        caption: t("resources.owner"),
        headers: [
          t("resources.def.kind"),
          t("resources.col.name"),
          t("resources.field.uid"),
          t("resources.owner.controller"),
        ],
        rows: owners.items.map((owner, index) => ({
          key: owner.uid.value ?? `${index}`,
          cells: [
            valueText(t, owner.kind),
            owner.link && onResourceLink ? (
              <button
                type="button"
                className="linkish"
                onClick={() =>
                  onResourceLink(owner.link as ResourceBuiltinLink)
                }
              >
                {valueText(t, owner.name)}
              </button>
            ) : (
              valueText(t, owner.name)
            ),
            valueText(t, owner.uid),
            boolText(t, owner.controller),
          ],
        })),
      },
      children: <OmittedNote value={owners} unit={t("resources.owner")} />,
    });
  }

  const service = builtin.service;
  if (service) {
    const external =
      service.type.state === "ok" && service.type.value === "ExternalName";
    sections.push({
      key: "service",
      title: t("resources.service"),
      rows: [
        [t("resources.cond.type"), valueText(t, service.type)],
        ...(!external
          ? [
              [
                t("resources.service.clusterIPs"),
                listText(
                  t,
                  service.clusterIPs,
                  t("resources.service.clusterIPs"),
                ),
              ] as [string, string],
            ]
          : []),
        [t("resources.service.selector"), boolText(t, service.selectorPresent)],
        ...(external
          ? [
              [
                t("resources.service.externalName"),
                valueText(t, service.externalName),
              ] as [string, string],
            ]
          : []),
      ],
      table: {
        caption: t("resources.service.ports"),
        headers: [
          t("resources.col.name"),
          t("resources.service.port"),
          t("resources.service.targetPort"),
          t("resources.service.protocol"),
        ],
        rows: service.ports.items.map((port, index) => ({
          key: `${port.name.value ?? "port"}-${port.port}-${index}`,
          cells: [
            valueText(t, port.name),
            port.port,
            targetPortText(t, port.targetPort),
            valueText(t, port.protocol),
          ],
        })),
      },
      children: (
        <>
          <OmittedNote
            value={service.ports}
            unit={t("resources.service.ports")}
          />
          {service.endpointReady && (
            <StateBox
              state={service.endpointReady.state}
              text={t(ENDPOINT_REASON_ID[service.endpointReady.reasonCode])}
              error={
                service.endpointReady.reasonCode !==
                "endpoint_ready_count_not_exposed"
              }
            />
          )}
        </>
      ),
    });
  }

  const ingress = builtin.ingress;
  if (ingress) {
    const paths = ingress.rules.items.flatMap((rule, ruleIndex) =>
      rule.paths.items.map((path, pathIndex) => ({
        rule,
        path,
        key: `${ruleIndex}/${pathIndex}`,
      })),
    );
    sections.push({
      key: "ingress",
      title: t("resources.ingress"),
      rows: [
        [
          t("resources.ingress.tls"),
          ingress.tls.items.length
            ? ingress.tls.items
                .map(
                  (entry) =>
                    `${listText(t, entry.hosts, t("resources.ingress.host"))} → ${valueText(t, entry.secretName)}`,
                )
                .join("; ")
            : t("resources.job.absent"),
        ],
        [
          t("resources.ingress.loadBalancer"),
          ingress.loadBalancer.items.length
            ? ingress.loadBalancer.items
                .map((entry) =>
                  [valueText(t, entry.ip), valueText(t, entry.hostname)].join(
                    " / ",
                  ),
                )
                .join("; ")
            : t("resources.job.absent"),
        ],
      ],
      table: {
        caption: t("resources.ingress.rules"),
        headers: [
          t("resources.ingress.host"),
          t("resources.ingress.path"),
          t("resources.ingress.pathType"),
          t("resources.ingress.backend"),
        ],
        rows: paths.map(({ rule, path, key }) => ({
          key,
          cells: [
            valueText(t, rule.host),
            valueText(t, path.path),
            valueText(t, path.pathType),
            <div key="backend">
              {backendText(t, path.backend)}
              <OmittedNote
                value={path.backend}
                unit={t("resources.ingress.backend")}
              />
            </div>,
          ],
        })),
      },
      children: (
        <>
          <OmittedNote
            value={ingress.rules}
            unit={t("resources.ingress.rules")}
          />
          {ingress.rules.items.map((rule, index) => (
            <OmittedNote
              key={index}
              value={rule.paths}
              unit={`${t("resources.ingress.path")} (${valueText(t, rule.host)})`}
            />
          ))}
          <OmittedNote value={ingress.tls} unit={t("resources.ingress.tls")} />
          <OmittedNote
            value={ingress.loadBalancer}
            unit={t("resources.ingress.loadBalancer")}
          />
        </>
      ),
    });
  }

  const pvc = builtin.persistentVolumeClaim;
  if (pvc)
    sections.push({
      key: "pvc",
      title: t("resources.pvc"),
      rows: [
        [t("resources.field.phase"), pvcPhaseText(t, pvc.phase)],
        [t("resources.field.storageClass"), valueText(t, pvc.storageClassName)],
        [t("resources.pvc.accessModes"), accessModesText(t, pvc.accessModes)],
        [t("resources.pvc.requested"), valueText(t, pvc.requestedStorage)],
        [t("resources.pvc.capacity"), valueText(t, pvc.capacityStorage)],
        [t("resources.pvc.volume"), valueText(t, pvc.volumeName)],
      ],
      table: {
        caption: t("resources.condition"),
        headers: [
          t("resources.cond.type"),
          t("resources.cond.status"),
          t("resources.cond.reason"),
          t("resources.ev.msg"),
          t("resources.at"),
        ],
        rows: pvc.conditions.items.map((condition) => ({
          key: condition.type,
          cells: [
            condition.type,
            t(CONDITION_STATUS_ID[condition.status]),
            valueText(t, condition.reason),
            valueText(t, condition.message),
            valueText(t, condition.lastTransitionTime),
          ],
        })),
      },
      children: (
        <>
          <OmittedNote value={pvc.conditions} unit={t("resources.condition")} />
          <StateBox
            state={pvc.metric.state}
            text={t("resources.metric.generation_safe_identity_not_proven")}
            error
          />
        </>
      ),
    });

  const pv = builtin.persistentVolume;
  if (pv) {
    const rows: [string, React.ReactNode][] = [
      [t("resources.field.phase"), valueText(t, pv.phase)],
      [t("resources.pvc.capacity"), valueText(t, pv.capacityStorage)],
      [t("resources.pv.reclaimPolicy"), valueText(t, pv.reclaimPolicy)],
      [t("resources.field.storageClass"), valueText(t, pv.storageClassName)],
    ];
    if (pv.claimRef)
      rows.push([
        t("resources.pv.claimRef"),
        `${valueText(t, pv.claimRef.namespace)}/${valueText(t, pv.claimRef.name)} · ${valueText(t, pv.claimRef.uid)}`,
      ]);
    sections.push({ key: "pv", title: t("resources.pv"), rows });
  }

  const storageClass = builtin.storageClass;
  if (storageClass)
    sections.push({
      key: "storageClass",
      title: t("resources.storageClass"),
      rows: [
        [
          t("resources.storageClass.provisioner"),
          valueText(t, storageClass.provisioner),
        ],
        [
          t("resources.pv.reclaimPolicy"),
          valueText(t, storageClass.reclaimPolicy),
        ],
        [
          t("resources.storageClass.bindingMode"),
          valueText(t, storageClass.volumeBindingMode),
        ],
        [
          t("resources.storageClass.default"),
          boolText(t, storageClass.isDefault),
        ],
      ],
    });

  const account = builtin.serviceAccount;
  if (account)
    sections.push({
      key: "serviceAccount",
      title: t("resources.serviceAccount"),
      rows: [
        [
          t("resources.serviceAccount.secrets"),
          listText(t, account.secrets, t("resources.serviceAccount.secrets")),
        ],
        [
          t("resources.serviceAccount.imagePullSecrets"),
          listText(
            t,
            account.imagePullSecrets,
            t("resources.serviceAccount.imagePullSecrets"),
          ),
        ],
        [
          t("resources.serviceAccount.automount"),
          boolText(t, account.automountServiceAccountToken),
        ],
      ],
    });

  return (
    <>
      {sections.map(({ key, ...section }) => (
        <DetailSection key={key} {...section} />
      ))}
      {relations && (
        <RelationsSection
          t={t}
          relations={relations}
          onResourceLink={onResourceLink}
        />
      )}
    </>
  );
}
